import React, { useState, useEffect } from 'react';
import {
Table,
TableBody,
TableCell,
TableContainer,
TableHead,
TableRow,
Paper,
TextField,
Button,
Dialog,
DialogTitle,
DialogContent,
DialogActions,
IconButton,
Tooltip,
Grid,
Typography,
Pagination,
Slide,
Fade,
InputAdornment,
Snackbar,
Alert,
Box,
CircularProgress,
Chip,
MenuItem,
Select,
FormControl,
InputLabel
} from '@mui/material';
import {
Add as AddIcon,
Edit as EditIcon,
Delete as DeleteIcon,
Search as SearchIcon,
Refresh as RefreshIcon,
Visibility as VisibilityIcon,
Visibility as ViewIcon,
Download as DownloadIcon,
Person as PersonIcon
} from '@mui/icons-material';
import { styled, useTheme } from '@mui/material/styles';
import useMediaQuery from '@mui/material/useMediaQuery';
import { TEXT_CONNECTION_ERROR } from '../utilities/Text';
import { APIApplicationList, APIApplicationUpdate, APIApplicationDelete, headers, APIDownloadCv, APIViewCv, APIBaseCv } from '../utilities/APIs';
import useCustomToast from '../hooks/useToast';
import axios from 'axios';
import { formatNumberWithComma } from '../utilities/Functions';
import { getUserRole, PERMISSIONS, ROLE_PERMISSIONS } from '../utilities/Permission';

const Applications = () => {
const theme = useTheme();
const isMobile = useMediaQuery(theme.breakpoints.down('sm'));

// State
const [loading, setLoading] = useState(true);
const [openEdit, setOpenEdit] = useState(false);
const [openView, setOpenView] = useState(false);
const [openDelete, setOpenDelete] = useState(false);
const [currentApplication, setCurrentApplication] = useState(null);
const [page, setPage] = useState(1);
const [rowsPerPage, setRowsPerPage] = useState(10);
const [searchTerm, setSearchTerm] = useState('');
const [error, setError] = useState(null);
const [postStatus, setPostStatus] = useState('');
const [updateId, setUpdateId] = useState('');
const showToast = useCustomToast();
const [pageState, setPageState] = useState({
    pageData: [],
});
const [snackbar, setSnackbar] = useState({
    open: false,
    message: '',
    severity: 'success'
});

// Form data - only status
const [formData, setFormData] = useState({
    status: ''
});

// Permissions
const [userRole, setUserRole] = useState('');
useEffect(() => {
    const role = getUserRole();
    setUserRole(role);
}, []);

// Helper function
const hasPermission = (permission) => {
    return ROLE_PERMISSIONS[userRole]?.includes(permission) || false;
};

useEffect(() => {
    fetchData();
}, []);

const fetchData = async () => {
    setLoading(true);
    setError(null);

    try {
    const response = await axios.get(APIApplicationList, { headers });
    const data = Array.isArray(response.data.data) ? response.data.data.reverse() : [];
    
    setPageState(prev => ({
        ...prev,
        pageData: data,
    }));
    } catch (error) {
    let errorMessage = "Failed to load application data";

    if (error.response) {
        errorMessage = error.response.data.message || error.message;
    } else if (error.code === "ERR_NETWORK") {
        errorMessage = TEXT_CONNECTION_ERROR;
    }

    setError(errorMessage);
    console.log(errorMessage);
    showToast("Error", errorMessage, "error");
    } finally {
    setLoading(false);
    }
};

// Filter applications based on search term
const filteredApplications = pageState.pageData.filter(application => {
    const matchesSearch = 
    application.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    application.email.toLowerCase().includes(searchTerm.toLowerCase()) ||
    application.position.toLowerCase().includes(searchTerm.toLowerCase()) ||
    application.phone.includes(searchTerm);
    
    return matchesSearch;
});

// Pagination
const count = Math.ceil(filteredApplications.length / rowsPerPage);
const indexOfLastRow = page * rowsPerPage;
const indexOfFirstRow = indexOfLastRow - rowsPerPage;
const currentRows = filteredApplications.slice(indexOfFirstRow, indexOfLastRow);

const handleChangePage = (event, newPage) => {
    setPage(newPage);
};

const handleChangeRowsPerPage = (event) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(1);
};

// Form handling
const handleInputChange = (e) => {
    const { name, value } = e.target;
    setFormData(prev => ({
    ...prev,
    [name]: value
    }));
};

// Dialog handlers
const handleOpenView = (application) => {
    setCurrentApplication(application);
    setOpenView(true);
};

const handleOpenEdit = (application) => {
    setFormData({
    status: application.status
    });
    setCurrentApplication(application);
    setUpdateId(application.id);
    setPostStatus("UPDATE");
    setOpenEdit(true);
};

const handleOpenDelete = (application) => {
    setCurrentApplication(application);
    setUpdateId(application.id);
    setPostStatus("DELETE");
    setOpenDelete(true);
};

// Prevent closing when clicking outside the dialog (backdrop click)
const handleClose = (event, reason) => {
    if (reason === 'backdropClick') {
    return;
    }
    setOpenEdit(false);
    setOpenView(false);
    setOpenDelete(false);
};

const handleRefresh = () => {
    setLoading(true);
    fetchData();
    setSnackbar({ open: true, message: 'Application data refreshed', severity: 'info' });
};

const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
};

const validateForm = () => {
    const errors = [];
    if (!formData.status.trim()) errors.push('Status is required');

    if (errors.length > 0) {
    showToast("Validation Error", errors.join('\n'), "warning");
    return false;
    }
    return true;
};

const handleSubmit = async (e) => {
    e.preventDefault();
    if (postStatus !== "DELETE") {
    if (!validateForm()) return;
    }
    
    const formDataToSend = new FormData();
    formDataToSend.append('status', formData.status);
    formDataToSend.append('updateId', updateId);

    try {
    let postRequest;
    if (postStatus === "UPDATE") {
        postRequest = await axios.post(APIApplicationUpdate, formDataToSend, { headers });
    } else if (postStatus === "DELETE") {
        postRequest = await axios.post(APIApplicationDelete, formDataToSend, { headers });
    }

    const response = postRequest.data.status;
    const message = postRequest.data.message;

    if (response === "success") {
        handleClose();
        fetchData();
        showToast("Success", message, "success");
    } else {
        showToast("Error", "Unknown Server Error, Record Not Updated", "error");
    }
    } catch (error) {
    if (error.response) {
        if (error.response.status === 422) {
        const errors = error.response.data.errors;
        let errorMessages = [];

        for (const field in errors) {
            errorMessages.push(...errors[field]);
        }

        showToast(
            "Validation Error",
            errorMessages.join("\n"),
            "error"
        );
        } else {
        showToast(
            "Error",
            error.response.data.message || "An error occurred",
            "error"
        );
        }
    } else if (error.code === "ERR_NETWORK") {
        showToast("Connection Error", TEXT_CONNECTION_ERROR, "error");
    } else {
        showToast("Error", "An unexpected error occurred", "error");
    }
    }
};

const handleDownloadCV = async (applicationId, applicationName) => {
    try {
    const response = await axios.get(`${APIBaseCv}${applicationId}${APIDownloadCv}`, {
        headers,
        responseType: 'blob'
    });
    
    // Create blob link to download
    const url = window.URL.createObjectURL(new Blob([response.data]));
    const link = document.createElement('a');
    link.href = url;
    
    // Get file extension from response headers or use default
    const contentDisposition = response.headers['content-disposition'];
    let fileName = `${applicationName}_CV.pdf`;
    
    if (contentDisposition) {
        const fileNameMatch = contentDisposition.match(/filename="?(.+)"?/);
        if (fileNameMatch && fileNameMatch[1]) {
        fileName = fileNameMatch[1];
        }
    }
    link.setAttribute('download', fileName);
    document.body.appendChild(link);
    link.click();
    link.remove();
    window.URL.revokeObjectURL(url);
    
    showToast("Success", "CV downloaded successfully", "success");
    } catch (error) {
    console.error('Error downloading CV:', error);
    if (error.response?.status === 404) {
        showToast("Error", "CV file not found on server", "error");
    } else {
        showToast("Error", "Failed to download CV", "error");
    }
    }
};

const handleViewCV = async (applicationId) => {
    try {
    // Open CV in new tab
    window.open(`${APIBaseCv}${applicationId}${APIViewCv}`, '_blank');
    } catch (error) {
    showToast("Error", "Failed to open CV", "error");
    }
};

const getStatusColor = (status) => {
    switch (status) {
    case 'approved':
        return 'success';
    case 'rejected':
        return 'error';
    case 'pending':
        return 'warning';
    default:
        return 'default';
    }
};

// Loading component
if (loading) {
    return (
    <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
        flexDirection="column"
        gap={2}
    >
        <CircularProgress />
        <Typography variant="body1">Loading applications...</Typography>
    </Box>
    );
}

// Error component
if (error) {
    return (
    <Box 
        display="flex" 
        justifyContent="center" 
        alignItems="center" 
        minHeight="200px"
        flexDirection="column"
        gap={2}
    >
        <Typography variant="h6" color="error">
        Error Loading Data
        </Typography>
        <Typography variant="body2" color="textSecondary">
        {error}
        </Typography>
        <Button 
        variant="contained" 
        color="primary" 
        onClick={fetchData}
        startIcon={<RefreshIcon />}
        >
        Retry
        </Button>
    </Box>
    );
}

return (
    <div style={{ marginTop: 15, padding: isMobile ? '8px' : '24px' }}>
    <Slide direction="down" in={true} mountOnEnter unmountOnExit>
        <Paper elevation={3} style={{
        padding: isMobile ? '16px' : '24px',
        borderRadius: '12px',
        marginBottom: '24px'
        }}>
        <Grid container justifyContent="space-between" alignItems="center" spacing={2}>
            <Grid item>
            <Typography variant="subtitle1">
                Applications Total:
                <Chip
                label={formatNumberWithComma(pageState.pageData.length)}
                color="primary"
                variant="outlined"
                style={{ marginLeft: '8px' }}
                />
            </Typography>
            </Grid>
        </Grid>

        <Grid container spacing={2} style={{ marginTop: '8px' }}>
            <Grid item xs={12} sm={6} md={4}>
            <TextField
                fullWidth
                variant="outlined"
                placeholder="Search applications..."
                value={searchTerm}
                onChange={(e) => {
                setSearchTerm(e.target.value);
                setPage(1);
                }}
                InputProps={{
                startAdornment: (
                    <InputAdornment position="start">
                    <SearchIcon />
                    </InputAdornment>
                ),
                style: { borderRadius: '8px' }
                }}
            />
            </Grid>
            <Grid item xs={12} sm={6} md={4}>
            <TextField
                fullWidth
                select
                value={rowsPerPage}
                onChange={handleChangeRowsPerPage}
                variant="outlined"
                style={{ borderRadius: '8px' }}
                SelectProps={{
                native: true,
                }}
            >
                <option value={5}>5 per page</option>
                <option value={10}>10 per page</option>
                <option value={25}>25 per page</option>
                <option value={50}>50 per page</option>
            </TextField>
            </Grid>
            <Grid item xs={12} md={4} style={{ display: 'flex', justifyContent: 'flex-end', gap: '8px' }}>
            <Tooltip title="Refresh data">
                <IconButton
                onClick={handleRefresh}
                style={{ backgroundColor: theme.palette.grey[200], borderRadius: '8px' }}
                >
                <RefreshIcon />
                </IconButton>
            </Tooltip>
            </Grid>
        </Grid>
        </Paper>
    </Slide>

    <Fade in={!loading} timeout={500}>
        <div>
        <TableContainer component={Paper} elevation={3} style={{ borderRadius: '12px', marginBottom: '24px' }}>
            <Table>
            <TableHead>
                <TableRow style={{ backgroundColor: theme.palette.primary.main }}>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>#</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Name</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Email</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Phone</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Position</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Status</TableCell>
                <TableCell style={{ color: 'white', fontWeight: 'bold' }}>Actions</TableCell>
                </TableRow>
            </TableHead>
            <TableBody>
                {currentRows.length > 0 ? (
                currentRows.map((application, index) => (
                    <StyledTableRow key={application.id}>
                    <TableCell>{(page - 1) * rowsPerPage + index + 1}</TableCell>
                    <TableCell>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PersonIcon color="primary" />
                        <Typography variant="body2" fontWeight="medium">
                            {application.name}
                        </Typography>
                        </Box>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2">
                        {application.email}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2">
                        {application.phone}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Typography variant="body2">
                        {application.position}
                        </Typography>
                    </TableCell>
                    <TableCell>
                        <Chip
                        label={application.status}
                        color={getStatusColor(application.status)}
                        variant="filled"
                        size="small"
                        />
                    </TableCell>
                    <TableCell>
                        <div style={{ display: 'flex', flexWrap: 'wrap' }}>
                        <Tooltip title="View Details">
                            <ActionButton
                            variant="contained"
                            color="info"
                            size="small"
                            onClick={() => handleOpenView(application)}
                            style={{ backgroundColor: '#17a2b8' }}
                            >
                            <ViewIcon fontSize="small" />
                            </ActionButton>
                        </Tooltip>
                        <Tooltip title="Download CV">
                            <ActionButton
                            variant="contained"
                            color="secondary"
                            size="small"
                            onClick={() => handleDownloadCV(application.id, application.name)}
                            style={{ backgroundColor: '#6c757d' }}
                            >
                            <DownloadIcon fontSize="small" />
                            </ActionButton>
                        </Tooltip>
                        <Tooltip title="Preview CV">
                            <ActionButton
                            variant="contained"
                            color="primary"
                            size="small"
                            onClick={() => handleViewCV(application.id)}
                            style={{ backgroundColor: '#28a745' }}
                            >
                            <VisibilityIcon fontSize="small" />
                            </ActionButton>
                        </Tooltip>
                        {hasPermission(PERMISSIONS.ALL) && (
                            <>
                            <Tooltip title="Edit Status">
                                <ActionButton
                                variant="contained"
                                color="primary"
                                size="small"
                                onClick={() => handleOpenEdit(application)}
                                >
                                <EditIcon fontSize="small" />
                                </ActionButton>
                            </Tooltip>
                            <Tooltip title="Delete">
                                <ActionButton
                                variant="contained"
                                color="error"
                                size="small"
                                onClick={() => handleOpenDelete(application)}
                                >
                                <DeleteIcon fontSize="small" />
                                </ActionButton>
                            </Tooltip>
                            </>
                        )}
                        </div>
                    </TableCell>
                    </StyledTableRow>
                ))
                ) : (
                <TableRow>
                    <TableCell colSpan={7} style={{ textAlign: 'center', padding: '40px' }}>
                    <Typography variant="subtitle1">
                        {searchTerm ? 'No matching applications found' : 'No application records available'}
                    </Typography>
                    </TableCell>
                </TableRow>
                )}
            </TableBody>
            </Table>
        </TableContainer>

        {filteredApplications.length > 0 && (
            <div style={{ display: 'flex', justifyContent: 'center', marginTop: '20px' }}>
            <Pagination
                count={count}
                page={page}
                onChange={handleChangePage}
                color="primary"
                shape="rounded"
                showFirstButton
                showLastButton
            />
            </div>
        )}
        </div>
    </Fade>

    {/* View Application Dialog */}
    <Dialog
        open={openView}
        onClose={handleClose}
        fullWidth
        maxWidth="md"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Application Details</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        {currentApplication && (
            <Grid container spacing={3}>
            <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Name:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                {currentApplication.name}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Email:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                {currentApplication.email}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Phone:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                {currentApplication.phone}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Position:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                {currentApplication.position}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Experience:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1 }}>
                {currentApplication.experience}
                </Typography>
            </Grid>
            <Grid item xs={12} sm={6}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Status:
                </Typography>
                </Box>
                <Box sx={{ p: 1 }}>
                <Chip
                    label={currentApplication.status}
                    color={getStatusColor(currentApplication.status)}
                    variant="filled"
                />
                </Box>
            </Grid>
            <Grid item xs={12}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 1 }}>
                <Typography variant="subtitle1" fontWeight="bold" color="textSecondary">
                    Cover Letter:
                </Typography>
                </Box>
                <Typography variant="body1" sx={{ p: 1, backgroundColor: 'grey.50', borderRadius: 1, minHeight: '100px' }}>
                {currentApplication.coverLetter}
                </Typography>
            </Grid>
            </Grid>
        )}
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="primary" variant="contained">
            Close
        </Button>
        </DialogActions>
    </Dialog>

    {/* Edit Application Dialog */}
    <Dialog
        open={openEdit}
        onClose={handleClose}
        fullWidth
        maxWidth="sm"
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Update Application Status</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px' }}>
        <form onSubmit={handleSubmit}>
            <Grid container spacing={2}>
            <Grid item xs={12}>
                <FormControl fullWidth variant="outlined">
                <InputLabel>Status</InputLabel>
                <Select
                    name="status"
                    value={formData.status}
                    onChange={handleInputChange}
                    label="Status"
                    required
                >
                    <MenuItem value="pending">Pending</MenuItem>
                    <MenuItem value="approved">Approved</MenuItem>
                    <MenuItem value="rejected">Rejected</MenuItem>
                </Select>
                </FormControl>
            </Grid>
            </Grid>
        </form>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} color="error" variant="outlined">
            Cancel
        </Button>
        <Button onClick={handleSubmit} color="primary" variant="contained">
            Update Status
        </Button>
        </DialogActions>
    </Dialog>

    {/* Delete Confirmation Dialog */}
    <Dialog
        open={openDelete}
        onClose={handleClose}
        maxWidth="xs"
        fullWidth
        TransitionComponent={Slide}
        transitionDuration={300}
    >
        <DialogTitle style={{ borderBottom: `1px solid ${theme.palette.divider}` }}>
        <Typography variant="h6" style={{ fontWeight: 600 }}>Confirm Delete</Typography>
        </DialogTitle>
        <DialogContent style={{ paddingTop: '20px', textAlign: 'center' }}>
        <Typography variant="body1" style={{ marginBottom: '16px' }}>
            Are you sure you want to delete this application?
        </Typography>
        <Typography variant="h6" color="error" style={{ fontWeight: 600 }}>
            {currentApplication?.name}
        </Typography>
        <Typography variant="body2" style={{ marginTop: '8px' }}>
            Position: {currentApplication?.position}
        </Typography>
        <Typography variant="caption" display="block" style={{ marginTop: '8px' }}>
            This action cannot be undone and will delete the CV file.
        </Typography>
        </DialogContent>
        <DialogActions style={{ borderTop: `1px solid ${theme.palette.divider}`, padding: '16px 24px' }}>
        <Button onClick={handleClose} variant="outlined" color="primary">
            Cancel
        </Button>
        <Button onClick={handleSubmit} variant="contained" color="error" startIcon={<DeleteIcon />}>
            Delete
        </Button>
        </DialogActions>
    </Dialog>

    {/* Snackbar for notifications */}
    <Snackbar
        open={snackbar.open}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'right' }}
        TransitionComponent={Fade}
    >
        <Alert
        onClose={handleCloseSnackbar}
        severity={snackbar.severity}
        variant="filled"
        sx={{ width: '100%' }}
        >
        {snackbar.message}
        </Alert>
    </Snackbar>
    </div>
);
};

export default Applications;

// Styled components
const StyledTableRow = styled(TableRow)(({ theme }) => ({
'&:nth-of-type(odd)': {
    backgroundColor: theme.palette.action.hover,
},
'&:hover': {
    backgroundColor: theme.palette.action.selected,
    transition: 'background-color 0.3s ease',
},
}));

const ActionButton = styled(Button)(({ theme }) => ({
margin: theme.spacing(0.5),
minWidth: 'auto',
}));